import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { TeamMembersAPI } from '@makyoapp/makyo-sdk/api/Teams'
import { OrgMemberRole, OrgPermission, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { TeamItemAdapter } from '@makyoapp/makyo-common'
import { ArrowDown16, Edit16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { TeamCard as BaseTeamCard, DeleteModal } from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { toasted } from '@/lib/toast'
import { useDeleteOrTransferTeam, useEditTeam } from '@/resolvers/org/teams/api'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { ORG_ROUTE, ORG_TEAM_ROUTE } from '@/shared/constants'
import { DeleteTeamModal } from '@/components/DeleteTeamModal'

const BasicInfoEditModal = lazyModal(() => import('@/components/BasicInfoEditModal'))

interface TeamCardProps {
  /**
   * the item
   */
  team: TeamItemAdapter
  /**
   * set leave team action
   */
  setLeaveTeam: (team: TeamItemAdapter) => void
  /**
   * set delete team action
   */
  setDeleteTeam: (team: TeamItemAdapter) => void
  /**
   * set edit team action
   */
  setEditTeam: (team: TeamItemAdapter) => void
}

/**
 * team card view extend from `makyo-ui/<TeamCard />` component
 *
 */
function TeamCard({ team, setLeaveTeam, setEditTeam, setDeleteTeam }: TeamCardProps) {
  const { t } = useTranslation()
  const { orgs, orgsPerms } = useOrgsContext()
  const { userData } = useAuth()

  // find user in team member
  const findTeamMe = team.members.find((tm) => tm.userId === userData?.id)

  // find user team role in this team or default to viewer
  const myRole = findTeamMe?.teamRole || TeamMemberRole.Viewer

  //myRole = myRole === OrgMemberRole.Owner ? TeamMemberRole.Admin : myRole

  let orgRole

  // find user in org member where this team belongs to
  const findOrgMe = orgs.find((o) => o.id === team.org.id)?.members.find((om) => om.userId === userData?.id)

  if (findOrgMe) {
    orgRole = findOrgMe.orgRole
  }

  // check if team is default team
  const isDefault = team.isDefault && findOrgMe ? findOrgMe.orgRole === OrgMemberRole.Owner : false

  // get the user permission of current org for this team
  const curOrgPerms = orgsPerms[team.org.id] || undefined

  // whether the user can edit this team
  const canDelete = !isDefault && curOrgPerms?.[OrgPermission.TeamsDeleteTeams]

  return (
    <BaseTeamCard
      id={team.id}
      name={team.name}
      avatar={team.avatar}
      renderLink={(node) => {
        return (
          <Link className='link' to={`${ORG_ROUTE}/${team.org.id}/${ORG_TEAM_ROUTE}/${team.id}`} replace={false}>
            {node}
          </Link>
        )
      }}
      teamRole={myRole}
      orgRole={orgRole}
      orgName={team.org.name}
      isDefault={isDefault}
      defaultTooltip={t("You can't leave the default teams of organizations you own")}
      menuBtnClassName='mk-team-menu-btn'
      menu={
        !(!isDefault || curOrgPerms?.[OrgPermission.TeamsEditTeamInfo] || canDelete) ? undefined : (
          <>
            {!isDefault && (
              <BaseTeamCard.MenuItem id={`${team.id}-leave-team-act`} onClick={() => setLeaveTeam(team)}>
                <ArrowDown16 />
                <div>{t('Leave Team')}</div>
              </BaseTeamCard.MenuItem>
            )}
            {curOrgPerms?.[OrgPermission.TeamsEditTeamInfo] && (
              <BaseTeamCard.MenuItem id={`${team.id}-edit-team-act`} onClick={() => setEditTeam(team)}>
                <Edit16 />
                <div>{t('Edit Team')}</div>
              </BaseTeamCard.MenuItem>
            )}
            {canDelete && (
              <BaseTeamCard.MenuItem
                id={`${team.id}-delete-team-act`}
                className='red'
                onClick={() => setDeleteTeam(team)}
              >
                <Trash16 />
                <div>{t('Delete Team')}</div>
              </BaseTeamCard.MenuItem>
            )}
          </>
        )
      }
    />
  )
}

interface ListTeamProps {
  id: string
  /**
   * the teams list
   */
  teams: TeamItemAdapter[]
  className?: string
  /**
   * action after updating the list of teams
   */
  onAfterUpdate?: () => void
}

/**
 * handle list team in card view
 */
export function ListTeamCard({ onAfterUpdate, ...props }: ListTeamProps) {
  const { t } = useTranslation()
  const [leaveTeam, setLeaveTeam] = useState<TeamItemAdapter | undefined>(undefined)

  const [deleteTeam, setDeleteTeam] = useState<TeamItemAdapter | undefined>(undefined)

  const [editTeam, setEditTeam] = useState<TeamItemAdapter | undefined>(undefined)

  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  const handleEditSubmit = useEditTeam({
    editTeam,
    onBeforeUpdate: useCallback(() => {
      setIsBtnLoading(true)
    }, [setIsBtnLoading]),
    onAfterUpdate: useCallback(() => {
      setEditTeam(undefined)
      setIsBtnLoading(false)
      onAfterUpdate?.()
    }, [onAfterUpdate, setIsBtnLoading]),
  })

  const { orgs } = useOrgsContext()
  const handleDeleteOrTransferTeam = useDeleteOrTransferTeam()
  const printAPIError = useReqError()

  return (
    <div className={clsx('grids-3 gap-8', props.className)}>
      {props.teams.map((team) => (
        <TeamCard
          key={team.id}
          team={team}
          setDeleteTeam={setDeleteTeam}
          setLeaveTeam={setLeaveTeam}
          setEditTeam={setEditTeam}
        />
      ))}
      {leaveTeam && (
        <DeleteModal
          isLoading={isBtnLoading}
          show={true}
          onHide={() => {
            setLeaveTeam(undefined)
          }}
          onSubmit={() => {
            if (leaveTeam) {
              setIsBtnLoading(true)
              TeamMembersAPI.ReqLeave(leaveTeam.id)
                .then(() => {
                  toasted(t('You have left {{team}}', { team: leaveTeam.name }))
                })
                .catch((e) => {
                  printAPIError(e)
                })
                .finally(() => {
                  setIsBtnLoading(false)
                  onAfterUpdate?.()
                  setLeaveTeam(undefined)
                })
            }
          }}
          withCancel
          title={t('Leave Team')}
          continueLabel={t('Yes, leave team')}
        >
          <div className='min-w-[320px]'>
            <div className='text-sm max-w-[320px] md:max-w-[640px]'>
              <div>
                {t('Are you sure you want to leave {{name}}?', {
                  name: leaveTeam.name,
                })}
              </div>
              <div className='text-m-m text-light'>
                {t('You will not be able to access apps and databases belonging to this team')}
              </div>
            </div>
          </div>
        </DeleteModal>
      )}
      {deleteTeam && (
        <DeleteTeamModal
          isLoading={isBtnLoading}
          setDeleteTeam={setDeleteTeam}
          deleteTeam={deleteTeam}
          onSubmit={(deleteTarget, transferTarget, done) => {
            handleDeleteOrTransferTeam(deleteTarget, transferTarget, {
              onBeforeUpdate: () => {
                setIsBtnLoading(true)
              },
              onAfterUpdate: () => {
                done?.()
                setIsBtnLoading(false)
                onAfterUpdate?.()
              },
              transferredNotice: t('Apps transferred!'),
              deletedNotice: t('Team removed!'),
            })
          }}
        />
      )}
      {editTeam && (
        <BasicInfoEditModal
          isLoading={isBtnLoading}
          id={`${props.id}-edit`}
          show={true}
          onHide={() => setEditTeam(undefined)}
          name={editTeam.name}
          avatar={editTeam.avatar}
          onSubmit={handleEditSubmit}
          title={t('Edit Team')}
        />
      )}
    </div>
  )
}
